import type { Task, TaskStatus } from '@/types';

/**
 * 根据当前日期自动判断任务状态
 * @param task 任务对象
 * @returns 自动判断后的状态
 */
export function autoCalculateTaskStatus(task: Task): TaskStatus {
  if (task.status === 'delayed' || task.status === 'delayed-completed' || task.status === 'completed') {
    return task.status;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(task.start);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(task.end);
  endDate.setHours(0, 0, 0, 0);

  if (today > endDate) {
    return 'completed';
  }

  if (today < startDate) {
    return 'not-started';
  }

  return 'in-progress';
}

/**
 * 批量自动计算所有任务状态
 * @param tasks 任务列表
 * @returns 更新后的任务列表
 */
export function autoCalculateAllTaskStatuses(tasks: Task[]): Task[] {
  return tasks.map(task => {
    const autoStatus = autoCalculateTaskStatus(task);
    // 只有当自动判断的状态与当前状态不同时才更新
    if (autoStatus !== task.status) {
      return { ...task, status: autoStatus };
    }
    return task;
  });
}

/**
 * 检查任务是否延期
 * @param task 任务对象
 * @returns 是否延期
 */
export function isTaskDelayed(task: Task): boolean {
  if (task.status === 'delayed') return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(task.end);
  endDate.setHours(0, 0, 0, 0);

  // 当前日期 > 结束日期 且 状态不是已完成
  return today > endDate && task.status !== 'completed';
}

/**
 * 获取任务状态颜色
 * @param status 任务状态
 * @returns 颜色类名
 */
export function getTaskStatusColor(status: TaskStatus): string {
  const colors: Record<TaskStatus, string> = {
    'not-started': 'bg-gray-200 text-gray-700',
    'in-progress': 'bg-blue-100 text-blue-700',
    'completed': 'bg-green-100 text-green-700',
    'delayed': 'bg-red-100 text-red-700',
    'delayed-completed': 'bg-orange-100 text-orange-700',
  };
  return colors[status] || colors['not-started'];
}

/**
 * 获取任务状态标签
 * @param status 任务状态
 * @returns 状态中文标签
 */
export function getTaskStatusLabel(status: TaskStatus): string {
  const labels: Record<TaskStatus, string> = {
    'not-started': '未开始',
    'in-progress': '进行中',
    'completed': '已完成',
    'delayed': '计划延迟',
    'delayed-completed': '延期完成',
  };
  return labels[status] || '未知';
}
